/**
 * Data layer for the COMPANY panel's Vision Board section.
 *
 * A board is a named collection of things somebody wants; an item is one thing
 * on it, either a word or a photo. Creating a board is one tap and filling it
 * in is the actual act, so "boards with nothing on them" is the figure this
 * section is built around — it is the only abandonment signal the feature
 * produces.
 *
 * Nothing anybody wrote on a board reaches this layer. No titles, no subtitles,
 * no item text, no stories, no uploaded images, no tags — the endpoints never
 * project them. The one piece of text that does come back is the display name
 * of a Shoorah idea a board was started from, which is approved catalogue copy
 * read from the catalogue itself, not the person's writing.
 *
 * Backed by the company/v1 vision-board-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page.
 *
 * The adapter is exported so the super-admin Vision Board hub, served by the
 * same builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyVisionBoardDashboard,
  getCompanyVisionBoardUsers,
  getCompanyVisionBoardUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Board block shared by every Vision Board dashboard. */
export const adaptVisionBoards = (data) => ({
  // Reach and volume
  totalBoards: data.totalBoards ?? 0,
  usersActive: data.usersActive ?? 0,
  totalItems: data.totalItems ?? 0,
  avgBoardsPerUser: data.avgBoardsPerUser ?? 0,
  avgItemsPerBoard: data.avgItemsPerBoard ?? 0,
  firstBoardDate: data.firstBoardDate || null,
  lastBoardDate: data.lastBoardDate || null,

  // Started versus actually filled in — the abandonment signal
  filledBoards: data.filledBoards ?? 0,
  emptyBoards: data.emptyBoards ?? 0,
  filledRate: data.filledRate ?? 0,

  activeBoards: data.activeBoards ?? 0,
  archivedBoards: data.archivedBoards ?? 0,

  // Intent to come back
  boardsWithReminder: data.boardsWithReminder ?? 0,
  reminderRate: data.reminderRate ?? 0,
  reminderBands: data.reminderBands || [],

  // What goes on a board — a count of words and photos, never their content
  itemMix: data.itemMix || [],
  stockPhotos: data.stockPhotos ?? 0,
  stockPhotoRate: data.stockPhotoRate ?? 0,

  boardSizeSplit: data.boardSizeSplit || [],
  volumeSplit: data.volumeSplit || [],

  // Shoorah's own catalogue copy
  boardsFromIdea: data.boardsFromIdea ?? 0,
  ideaRate: data.ideaRate ?? 0,
  topIdeas: data.topIdeas || [],

  weekdayBoards: data.weekdayBoards || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  boardsTrend: (data.dailyTrend || []).map((day) => day.boards || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanyVisionBoardDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyVisionBoardDashboard(department));
    return {
      ...adaptVisionBoards(data),
      // Company-only: the size of the population, so the page can say
      // "34 of 240 employees have made a board".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyVisionBoardDashboard error:', error);
    return null;
  }
};

// Matches the VisionBoardUsersTable fetcher contract.
export const fetchCompanyVisionBoardUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyVisionBoardUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyVisionBoardUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's board profile ({ user, itemMix, topIdeas,
 * boardHistory }). A history row is a date, a size, a reminder and a status —
 * never the board's name or what is on it. The backend 404s any user outside
 * this company, so no scoping is needed here.
 */
export const fetchCompanyVisionBoardUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyVisionBoardUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyVisionBoardUserDetail error:', error);
    return null;
  }
};
